import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MUIDataTable from 'mui-datatables';
import { MuiThemeProvider, createTheme, makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grow,
  TextField,
  IconButton,
  Icon
} from '@material-ui/core';
import { Breadcrumb, BackdropCircular } from 'components';
import { getAuditLogsApi, exportAuditLogsApi, getUsersAPI } from 'api';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';


const useStyles = makeStyles({
  clearButton: {
    cursor: 'pointer'
  }
});

export default function AuditLogs() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { auditLogs = {}, loadingAuditLogs} = useSelector((state) => state.audit_logs);
  const { currentStoreUsersEmails, profile } = useSelector((state) => state.user);
  const { logs = [], page, limit = 10, totalCount, sortField, sortDir, email, fromDate, toDate, store, reqBody} = auditLogs;
  const [rowsPerPage, setRowsPerPage] = useState(limit);
  const [sortData, setSortData] = useState({sortField: 'createdAt', sortDir: 'desc'});
  const [filterData, setFilterData] = useState({ email: '', fromDate: '', toDate: '', store: '', action: '', reqBody: '' });

  const handleChangePage = (newPage) => {
    const {sortField, sortDir} = sortData;
    const { email, fromDate, toDate, store, action, reqBody } = filterData;
    getAuditLogsApi({dispatch, page: newPage, limit: rowsPerPage, sortField, sortDir, email, fromDate, toDate, store, action, reqBody});
  };

  const handleChangeRowsPerPage = (newLimit) => {
    const {sortField, sortDir} = sortData;
    const { email, fromDate, toDate, store, action, reqBody } = filterData;
    const newLastPage = Math.ceil(totalCount / newLimit);
    const pageNumber = page > newLastPage ? newLastPage : page;
    getAuditLogsApi({dispatch, page: pageNumber, limit: newLimit, sortField, sortDir, email, fromDate, toDate, store, action, reqBody});
    setRowsPerPage(newLimit);
  };

  const handleChangeSort = (sortField, sortDir) => {
    const { email, fromDate, toDate, store, action, reqBody } = filterData;
    setSortData({...sortData, sortField, sortDir});
    getAuditLogsApi({dispatch, page, limit: rowsPerPage, sortField, sortDir, email, fromDate, toDate, store, action, reqBody});
  };

  const handleFilterSubmit = (applyNewFilters) => {
    const {sortField, sortDir} = sortData;
    const [, email, dateFrom, dateTo,,,,, store] = applyNewFilters();
    const fromDate = dateFrom.length ? dateFrom[0] : '';
    const toDate = dateTo.length ? dateTo[0] : '';

    setFilterData({...filterData, fromDate, toDate, store, email});
    getAuditLogsApi({dispatch, page, limit: rowsPerPage, email, sortField, sortDir, fromDate, toDate, store, reqBody: filterData.reqBody });
  };

  useEffect(() => {
    getAuditLogsApi({dispatch});
    getUsersAPI({dispatch});
  }, []);

  const columns = [
    {
      name: 'createdAt',
      label: 'Created At',
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const createdAt = new Date(logs[dataIndex].createdAt);
          return (
            <>{createdAt.toLocaleString()}</>
          );
        }
      }
    },
    {
      name: 'userEmail',
      label: 'User Email',
      options: {
        filter: true,
        filterType: 'custom',
        customFilterListOptions: {
          render: (v) => {
            if (v[0] === '') {
              v = [];
            }

            return v;
          },
          update: (filterList, _filterPos, index) => {
            getAuditLogsApi({dispatch, page, limit, sortField, sortDir, email, fromDate, toDate, store, reqBody});
            filterList[index] = [];
            return filterList;
          }
        },
        filterOptions: {
          fullWidth: true,
          display: (filterList, onChange, index, column) => {
            return (
              <FormControl>
                <InputLabel shrink htmlFor="select-multiple-chip">
                  User Email
                </InputLabel>
                <Select
                  value={filterList[index] || ''}
                  displayEmpty
                  onChange={(event) => {
                    const { value } = event.target;
                    filterList[index][0] = value;
                    onChange(filterList[index], index, column);
                    setFilterData({...filterData, email: value});
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {currentStoreUsersEmails.map((item) => (<MenuItem key={item} value={item}>{item}</MenuItem>))}
                </Select>
              </FormControl>
            );
          }
        }
      }
    },
    {
      name: 'dateFrom',
      label: 'Date From',
      options: {
        display: false,
        viewColumns: false,
        filter: true
      }
    },
    {
      name: 'dateTo',
      label: 'Date To',
      options: {
        display: false,
        viewColumns: false,
        filter: true
      }
    },
    {
      name: 'method',
      label: 'Method',
      options: {
        filter: false
      }
    },
    {
      name: 'action',
      label: 'Path',
      options: {
        filter: false
      }
    },
    {
      name: 'area',
      label: 'Area',
      options: {
        filter: false
      }
    },
    {
      name: 'action',
      label: 'Item',
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const { action } = logs[dataIndex];
          return <div className="capitalize">{action?.replace('/v1/', '')?.replace(/\//g, ' ')}</div>;
        }
      }
    },
    {
      name: 'reqBody',
      label: 'Item Data',
      options: {
        filter: true,
        filterType: 'custom',
        customFilterListOptions: {
          render: (v) => {
            if (v[0] === '') {
              v = [];
            }

            return v;
          },
          update: (filterList, _filterPos, index) => {
            getAuditLogsApi({dispatch, page, limit, sortField, sortDir, email, fromDate, toDate, store, reqBody});
            filterList[index] = [];
            return filterList;
          }
        },
        filterOptions: {
          fullWidth: true,
          display: (filterList, onChange, index, column) => {
            return (
              <TextField
                className="w-full"
                variant="standard"
                label="Item Data"
                value={filterList[index] || ''}
                onChange={(event) => {
                  const { value } = event.target;
                  filterList[index][0] = value;
                  onChange(filterList[index], index, column);
                  setFilterData({...filterData, reqBody: value});
                }}
              />
            );
          }
        },
        customBodyRenderLite: (dataIndex) => {
          const itemData = logs[dataIndex].reqBody;
          return <div className="whitespace-pre-wrap">{itemData}</div>;
        }
      }
    },
    {
      name: 'message',
      label: 'Message',
      options: {
        filter: false
      }
    },
    {
      name: 'referrerUrl',
      label: 'Referrer Url',
      options: {
        filter: false
      }
    },
    {
      name: 'ip',
      label: 'IP',
      options: {
        filter: false
      }
    },
    {
      name: 'success',
      label: 'Success',
      options: {
        display: false,
        viewColumns: false,
        filter: false
      }
    }
  ];

  const myTheme = createTheme({
    overrides: {
      MUIDataTable: {
        responsiveScroll: {
          overflowX: 'auto',
        }
      }
    }
  });

  return (
    <div className="flex-grow h-full scroll-y">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Settings', path: '/settings#go_live_requirements' },
            { name: 'Store Configurations', path: '/settings#store_configurations' },
            { name: 'Audit Logs' }
          ]}
        />
      </div>
      <div className="mx-10 mt-5 mb-25">
        <MuiThemeProvider theme={myTheme}>
          <MUIDataTable
            title={
              <Typography variant="h4" className="flex items-center">
                <span>User actions logs {profile.stores?.[0].storeUrl}</span>
              </Typography>}
            data={logs}
            columns={columns}
            options={{
              download: true,
              textLabels: {
                toolbar: {
                  downloadCsv: 'Click here to download your audit logs'
                }
              },
              onDownload: () => {
                const { email, fromDate, toDate, store, reqBody } = filterData;
                exportAuditLogsApi({dispatch, email, fromDate, toDate, store, reqBody });

                return false; // cancel download default csv file.
              },
              filter: true,
              filterType: 'custom',
              filterOptions: {
                names: [],
                logic(date, filters) {
                  const check = new Date(date);
                  const from = new Date(filters[0]);
                  const to = new Date(filters[1]);
                  from.setDate(from.getDate() + 1);
                  to.setDate(to.getDate() + 1);

                  if (filters[0] && filters[1] && check >= to && check <= from) {
                    return true;
                  } else if (filters[0] && check >= to) {
                    return true;
                  } else if (filters[1] && check <= from) {
                    return true;
                  }

                  return false;
                },
                display: (filterList, onChange, index, column) => (
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DateTimePicker
                      variant="inline"
                      label={column.label}
                      autoOk
                      value={filterList[index][0] || null}
                      format="yyyy/MM/dd HH:mm"
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          filterList[index][0] ?
                            <Icon
                              className={classes.clearButton}
                              onClick={() => {
                                filterList[index] = [];
                                setFilterData((prev) => ({...prev, column: null}));
                              }}
                            >
                              clear
                            </Icon> : null
                        )
                      }}
                      onChange={(event) => {
                        filterList[index][0] = new Date(event).toJSON();
                        onChange(filterList[index], index, column);
                      }}
                    />
                  </MuiPickersUtilsProvider>
                ),
              },
              print: false,
              pagination: true,
              viewColumns: true,
              rowsPerPage,
              rowsPerPageOptions: [10, 20, 40, 80, 100],
              responsive: 'standard',
              serverSide: true,
              search: true,
              customSearchRender: (
                searchText,
                handleSearch,
                hideSearch
              ) => {
                return (
                  <Grow appear in timeout={300}>
                    <TextField
                      variant="outlined"
                      size="small"
                      value={filterData.action}
                      onChange={(event) => setFilterData({...filterData, action: event.target.value})}
                      fullWidth
                      onKeyPress={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          getAuditLogsApi({dispatch, page, limit, sortField, sortDir, email, fromDate, toDate, store, action: filterData.action, reqBody});
                        }
                      }}
                      InputProps={{
                        style: {
                          paddingRight: 0,
                        },
                        startAdornment: (
                          <IconButton
                            className="mr-2"
                            onClick={() => {
                              getAuditLogsApi({dispatch, page, limit, sortField, sortDir, email, fromDate, toDate, store, action: filterData.action, reqBody});
                            }}
                          >
                            <Icon
                              fontSize="small"
                            >
                              search
                            </Icon>
                          </IconButton>
                        ),
                        endAdornment: (
                          <IconButton
                            onClick={() => {
                              filterData.action = '';
                              getAuditLogsApi({dispatch, page, limit, sortField, sortDir, email, fromDate, toDate, store, action: '', reqBody});
                              hideSearch();
                            }}
                          >
                            <Icon
                              fontSize="small"
                            >
                              clear
                            </Icon>
                          </IconButton>
                        ),
                      }}
                    />
                  </Grow>
                );
              },
              count: totalCount,
              confirmFilters: true,
              customFilterDialogFooter: (currentFilterList, applyNewFilters) => {
                return (
                  <div className="mt-10">
                    <Button variant="contained" onClick={() => handleFilterSubmit(applyNewFilters)}>Apply Filters</Button>
                  </div>
                );
              },
              onFilterChange: (column, filterList, type) => {
                if (type === 'chip') {
                  const newFilters = () => (filterList);
                  handleFilterSubmit(newFilters);
                }
              },
              onColumnSortChange: (changedColumn, direction) => handleChangeSort(changedColumn, direction),
              setRowProps: (row) => {
                return row[5] ? null : {className: 'bg-error'};
              },
              onChangePage: (currentPage) => handleChangePage(currentPage + 1),
              onChangeRowsPerPage: handleChangeRowsPerPage
            }}
          />
        </MuiThemeProvider>
        <BackdropCircular loading={loadingAuditLogs} />
      </div>

    </div>
  );
}
